import * as React from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import { Image } from "@lululemon/ecom-pattern-library-image";
import QuizQuestionTile from "./quiz-question-tile/quiz-question-tile";
import QuizDropDownGroup from "./quiz-question-dropdown/quiz-dropdown-group";
import styles from "./quiz-question.module.scss";
import QuizPagination from "../quiz-pagination/quiz-pagination";
import BraQuizDataContext from "../utils/quizDataContext";
import { getFlattedContextData } from "../utils";

const BraQuizQuestion = ({
  activeQuestionIndex,
  answers,
  isActiveQuestion,
  isEmbedded,
  isFirstStepVisited,
  isQuestionChanging,
  onAnswerReady,
  onInputFocus,
  onSaveAnswers,
  questionIndex,
  questionRef,
  fieldValidationError,
  setValidationErrorTrackingData,
}) => {
  const [showError, setShowError] = React.useState(false); // Question warning text
  const braQuizData = React.useContext(BraQuizDataContext);
  const [missingFields, setMissingFields] = React.useState({});
  const { questions, fields } = getFlattedContextData(braQuizData);
  const question = questions[questionIndex];
  const isFirstQuestion = questionIndex === 0;
  const tabIndex = isActiveQuestion ? "0" : "-1";
  const { image, title, subTitle, description, footNote, error } = question;
  const showPaginationImage = image && !!Object.keys(image).length;

  const questionFieldsData = React.useMemo(
    () =>
      question.fields.map((fieldName) => ({
        ...fields[fieldName],
        id: fieldName,
      })),
    [fields, question]
  );
  const isTile = questionFieldsData[0].type === "tile";

  const validateAnswers = () => {
    let shouldShowError = false;

    questionFieldsData.forEach((field) => {
      const selectedAnswer = answers[`${field.id}`];
      let optionsArray = [];

      if (field?.options?.[0]?.lllSize) {
        optionsArray = field.options;
      } else if (field.brands) {
        optionsArray = field.brands[`${answers.brands}`];
      }

      if (optionsArray.length) {
        const optionObject = optionsArray.filter(
          (option) => option.value === selectedAnswer
        );
        if (!optionObject?.[0]?.lllSize?.length) shouldShowError = true;
      }
    });

    return shouldShowError;
  };

  React.useEffect(() => {
    const getMissingFields = () => {
      const nonFilledFields = ({ id: fieldId }) =>
        answers[`${fieldId}`] === null;

      const toMissingFieldsObject = (prev, { id, label, type, hint }) => {
        const isTileAnswer = type === "tile";
        const details = `${
          isTileAnswer ? "Cup fit" : label
        } has not been selected`;
        const message = hint;

        return {
          ...prev,
          [id]: {
            validationErrorData: {
              isGuestFacing: true,
              details,
              message,
            },
          },
        };
      };

      return questionFieldsData
        .filter(nonFilledFields)
        .reduce(toMissingFieldsObject, {});
    };

    const validateSelectedFields = () => {
      const missingFields = getMissingFields();
      const areAllQuestionSelected = Object.keys(missingFields).length < 1;
      setMissingFields(missingFields);
      setValidationErrorTrackingData(
        Object.values(missingFields).map((value) => value.validationErrorData)
      );
      onAnswerReady(areAllQuestionSelected);
      setShowError(areAllQuestionSelected && validateAnswers());
    };

    if (isActiveQuestion) {
      validateSelectedFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, activeQuestionIndex, isActiveQuestion, questionFieldsData]);

  return (
    <div
      className={cs(styles.quizQuestion, {
        [styles.quizQuestionEmbedded]: isEmbedded,
        [styles.quizQuestionIsActive]: isActiveQuestion && !isQuestionChanging,
      })}
    >
      {showPaginationImage && !isEmbedded && (
        <div
          data-testid="first-question-container"
          className={cs(
            styles.quizFirstQuestion,
            styles.quizFirstQuestionIsActive,
            "row",
            "start-md"
          )}
        >
          <div
            className={cs(
              "lll-text-center",
              styles.quizFirstQuestionFirstPagination
            )}
          >
            <QuizPagination
              questionNumber={questionIndex + 1}
              autofocus={isActiveQuestion && isFirstStepVisited}
              isAbsolutePosition
            />
            <div className={cs(styles.quizFirstQuestionImageWrapper)}>
              <Image
                className={cs(styles.quizFirstQuestionImage)}
                src={image.imageUrl}
                maxWidth={920}
                prefix="bra-quiz-intro"
                alt={image.imageDescription}
                layout="fill"
                lazy={!isFirstQuestion}
              />
            </div>
          </div>
        </div>
      )}
      <h2
        className={cs("col-xs-10", styles.quizQuestionHeading, {
          [styles.quizQuestionHeadingEmbedded]: isEmbedded,
          [styles.quizQuestionHeadingMargin]: !isFirstQuestion,
          [styles.quizQuestionHeadingA11yImprovements]: !isEmbedded,
        })}
      >
        {title}
      </h2>

      <h3
        id={isTile ? questionFieldsData[0].id : ""}
        className={cs("col-xs-10", styles.quizQuestionSubHeading, {
          [styles.quizQuestionSubHeadingEmbedded]: isEmbedded,
        })}
      >
        {subTitle}
      </h3>

      {description && (
        <p
          className={cs("lll-text-body-2", styles.quizQuestionText, {
            [styles.quizQuestionTextEmbedded]: isEmbedded,
          })}
        >
          {description}
        </p>
      )}

      {isTile ? (
        <>
          <QuizQuestionTile
            options={questionFieldsData[0].options}
            questionID={questionFieldsData[0].id}
            onSaveAnswers={onSaveAnswers}
            tabIndex={tabIndex}
            questionRef={questionRef}
            onInputFocus={onInputFocus}
            isNotFirstStep={!isFirstQuestion}
            isEmbedded={isEmbedded}
            fieldValidationError={fieldValidationError}
          />
          {fieldValidationError && !isEmbedded && (
            <p
              role="alert"
              aria-live="assertive"
              tabIndex="-1"
              className={cs(
                styles.quizQuestionErrorTileWithA11y,
                "lll-text-body-2",
                "lll-text-error"
              )}
            >
              {
                missingFields[questionFieldsData[0].id]?.validationErrorData
                  .message
              }
            </p>
          )}
        </>
      ) : (
        <div
          className={cs("row", {
            "center-xs": !isEmbedded,
            [styles.quizQuestionDropDownEmbedded]: isEmbedded,
          })}
        >
          <div
            data-testid="quiz-question-dropdown"
            className={cs({
              "col-md-12 col-xs-12 col-sm-12": isEmbedded,
              "col-md-10 col-xs-10 col-sm-4": !isEmbedded,
            })}
          >
            <QuizDropDownGroup
              questionFieldsData={questionFieldsData}
              onSaveAnswers={onSaveAnswers}
              tabIndex={tabIndex}
              isEmbedded={isEmbedded}
              missingFields={missingFields}
              fieldValidationError={fieldValidationError}
            />
          </div>
          {footNote?.trim() && (
            <p
              data-testid="question-dropdown-sub-text"
              className={cs(styles.quizQuestionSubText, {
                [styles.quizQuestionSubTextEmbedded]: isEmbedded,
              })}
            >
              {footNote}
            </p>
          )}
        </div>
      )}

      {showError && Object.keys(error).length && (
        <>
          <h3
            className={cs(
              styles.quizQuestionError,
              styles.quizQuestionErrorHeading,
              {
                [styles.quizQuestionErrorEmbedded]: isEmbedded,
              }
            )}
          >
            {error.heading}
          </h3>
          <p
            className={cs(styles.quizQuestionError, {
              "lll-text-body-3": isEmbedded,
              [styles.quizQuestionErrorEmbedded]: isEmbedded,
            })}
          >
            {error.text}
          </p>
        </>
      )}
    </div>
  );
};

BraQuizQuestion.defaultProps = {
  fieldValidationError: false,
  isActiveQuestion: false,
  isEmbedded: false,
  isFirstStepVisited: false,
  isQuestionChanging: false,
};

BraQuizQuestion.propTypes = {
  isActiveQuestion: PropTypes.bool,
  isQuestionChanging: PropTypes.bool,
  isFirstStepVisited: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  answers: PropTypes.shape({
    brands: PropTypes.string,
    bandSizes: PropTypes.string,
    cupSizes: PropTypes.string,
    cupFit2: PropTypes.number,
    dressSizes: PropTypes.string,
    hookClasp: PropTypes.string,
  }).isRequired,
  activeQuestionIndex: PropTypes.number.isRequired,
  questionIndex: PropTypes.number.isRequired,
  questionRef: PropTypes.func.isRequired,
  onAnswerReady: PropTypes.func.isRequired,
  onSaveAnswers: PropTypes.func.isRequired,
  onInputFocus: PropTypes.func.isRequired,
  fieldValidationError: PropTypes.bool,
  setValidationErrorTrackingData: PropTypes.func.isRequired,
};

export default BraQuizQuestion;
